import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  addDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { firestore } from "firebaseConfig";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

//List of recipes
export const getrecipes = createAsyncThunk(
  "Recipe/getrecipes",
  async ({ uid }, thunkAPI) => {
    try {
      let data = [];
      const q = query(
        collection(firestore, "recipe"),
        where("adminId", "==", uid)
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        let timestamp = null;
        let updatedAt = null;
        if (doc.data().createdAt) {
          let createdAt = doc.data().createdAt;
          timestamp =
            createdAt?.nanoseconds / 1000000 + createdAt?.seconds * 1000;
          timestamp = moment.utc(timestamp);
        }

        if (doc.data().updatedAt) {
          updatedAt = doc.data().updatedAt;
          updatedAt =
            updatedAt?.nanoseconds / 1000000 + updatedAt?.seconds * 1000;
          updatedAt = moment.utc(updatedAt);
        }

        data.push({
          ...doc.data(),
          createdAt: timestamp,
          id: doc.id,
          updatedAt,
        });
      });

      data.sort(function (a, b) {
        let createdAt1 = a.createdAt._d;
        createdAt1 = createdAt1.seconds
          ? createdAt1?.nanoseconds / 1000000 + createdAt1?.seconds * 1000
          : createdAt1;

        let createdAt2 = b.createdAt._d;
        createdAt2 = createdAt2.seconds
          ? createdAt2?.nanoseconds / 1000000 + createdAt2?.seconds * 1000
          : createdAt2;
        return new Date(createdAt2) - new Date(createdAt1);
      });

      return [...data];
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const AddRecipe = createAsyncThunk(
  "Recipe/AddRecipe",
  async ({ formdata, uid }, thunkAPI) => {
    try {
      const q = query(
        collection(firestore, "recipe"),
        where("name", "==", formdata.name),
        where("adminId", "==", uid)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        return thunkAPI.rejectWithValue({
          message:
            "Recipe with this name already exists, choose different name",
        });
      }

      let data = {
        name: formdata.name ?? "",
        description: formdata.description ?? "",
        preparation: formdata.preparation ?? "",
        calories:
          !isNaN(formdata.calories) && formdata.calories !== null
            ? formdata.calories
            : "",
        weight:
          !isNaN(formdata.weight) && formdata.weight !== null
            ? formdata.weight
            : "",
        foodType: Array.isArray(formdata.foodType) ? formdata.foodType : [],
        ingredients: Array.isArray(formdata.ingredients)
          ? formdata.ingredients
          : [],
        groceryList: Array.isArray(formdata.groceryList)
          ? formdata.groceryList
          : [],
        nutritions: Array.isArray(formdata.nutritions)
          ? formdata.nutritions
          : [],
        howToTake: formdata.howToTake ?? "",
        unit: formdata.unit ?? "",
        qty: formdata.qty ?? "",
        foodCategory: Array.isArray(formdata.foodCategory)
          ? formdata.foodCategory
          : [],
        goesWell: Array.isArray(formdata.goesWell) ? formdata.goesWell : [],
        createdAt: new Date(),
        adminId: uid,
      };

      if (formdata.image) {
        if (formdata.image instanceof File) {
          const storage = getStorage();
          const storageRef = ref(
            storage,
            `recipeImages/${uid}/${Date.now()}-${formdata.image.name}`
          );
          // Upload image
          await uploadBytes(storageRef, formdata.image);
          // Get download URL
          const downloadURL = await getDownloadURL(storageRef);
          data.image = downloadURL;
        } else {
          data.image = formdata.image;
        }
      }

      if (
        isNaN(formdata.calories) ||
        formdata.calories === "" ||
        formdata.calories === null
      ) {
        delete data.calories;
      }

      if (
        isNaN(formdata.weight) ||
        formdata.weight === "" ||
        formdata.weight === null
      ) {
        delete data.weight;
      }

      const recipeRef = await addDoc(collection(firestore, "recipe"), {
        ...data,
      });
      return {
        ...formdata,
        foodCategory: formdata.foodCategory,
        unit: formdata.unit,
        id: recipeRef.id,
        createdAt: new Date(),
        image: data.image || "",
      };
    } catch (e) {
      console.log({ e });
      return thunkAPI.rejectWithValue(e);
    }
  }
);

//Add multiple recipes
export const AddMultipleRecipes = createAsyncThunk(
  "Recipe/AddMultipleRecipes",
  async ({ recipes, adminId }, thunkAPI) => {
    const addedRecipes = [];

    try {
      // Firestore allows max 10 items in 'in' clause
      const batchSize = 10;
      const batches = [];

      for (let i = 0; i < recipes.length; i += batchSize) {
        const names = recipes
          .slice(i, i + batchSize)
          .map((r) => r.name)
          .filter(Boolean);
        if (names.length > 0) batches.push(names);
      }

      // Check for duplicates
      const queries = batches.map((nameBatch) =>
        query(
          collection(firestore, "recipe"),
          where("name", "in", nameBatch),
          where("adminId", "==", adminId)
        )
      );

      const existingNames = [];

      for (const q of queries) {
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          existingNames.push(docSnap.data().name);
        });
      }

      if (existingNames.length > 0) {
        return thunkAPI.rejectWithValue({
          message: `These recipes already exist: ${existingNames.join(", ")}`,
        });
      }

      // Write in chunks (Firestore limit: 500 writes/batch)
      const writeBatchSize = 500;
      for (let i = 0; i < recipes.length; i += writeBatchSize) {
        const batch = writeBatch(firestore);
        const recipeBatch = recipes.slice(i, i + writeBatchSize);

        for (const recipe of recipeBatch) {
          const recipeId = uuidv4();
          const docRef = doc(firestore, "recipe", recipeId);
          const cleanRecipe = Object.fromEntries(
            Object.entries(recipe).filter(([_, v]) => v !== undefined)
          );

          addedRecipes.push({ ...cleanRecipe, id: recipeId });
          batch.set(docRef, { ...cleanRecipe });
        }

        await batch.commit();
      }

      return addedRecipes;
    } catch (e) {
      console.error("Error in AddMultipleRecipes:", e);
      return thunkAPI.rejectWithValue({
        message: e.message || "Something went wrong while adding recipes",
      });
    }
  }
);

//Edit Recipe
export const EditRecipe = createAsyncThunk(
  "Recipe/EditRecipe",
  async ({ formdata, uid }, thunkAPI) => {
    try {
      const q = query(
        collection(firestore, "recipe"),
        where("name", "==", formdata.name),
        where("adminId", "==", uid)
      );
      const querySnapshot = await getDocs(q);
      let check = false;

      querySnapshot.forEach((doc) => {
        if (doc.id !== formdata.id) {
          check = true;
        }
      });

      if (check) {
        return thunkAPI.rejectWithValue({
          message:
            "Recipe with this name already exists, choose different name",
        });
      }

      const docRef = doc(firestore, "recipe", formdata.id);

      let data = {
        name: formdata.name,
        description: formdata.description,
        preparation: formdata.preparation,
        foodType: formdata.foodType,
        ingredients: formdata.ingredients,
        groceryList: formdata.groceryList ?? [],
        nutritions: formdata.nutritions ?? [],
        calories: formdata.calories,
        weight: formdata.weight,
        howToTake: formdata.howToTake,
        foodCategory: formdata.foodCategory,
        goesWell: Array.isArray(formdata.goesWell) ? formdata.goesWell : [],
        unit: formdata.unit,
        qty: formdata.qty,
        updatedAt: new Date(),
      };

      if (formdata.image) {
        if (formdata.image instanceof File) {
          const storage = getStorage();
          const storageRef = ref(
            storage,
            `recipeImages/${uid}/${Date.now()}-${formdata.image.name}`
          );

          await uploadBytes(storageRef, formdata.image);

          const downloadURL = await getDownloadURL(storageRef);
          data.image = downloadURL;
        } else {
          data.image = formdata.image;
        }
      }

      // Remove invalid calories
      if (
        isNaN(formdata.calories) ||
        formdata.calories === "" ||
        formdata.calories === null
      ) {
        delete data.calories;
      }

      // Remove invalid weight
      if (
        isNaN(formdata.weight) ||
        formdata.weight === "" ||
        formdata.weight === null
      ) {
        delete data.weight;
      }

      await updateDoc(docRef, { ...data });

      return {
        id: formdata.id,
        name: formdata.name,
        description: formdata.description,
        preparation: formdata.preparation,
        calories: formdata.calories,
        weight: formdata.weight,
        foodType: formdata.foodType,
        ingredients: formdata.ingredients ?? [],
        groceryList: formdata.groceryList ?? [],
        nutritions: formdata.nutritions,
        howToTake: formdata.howToTake,
        unit: formdata.unit || formdata?.unit.value,
        qty: formdata.qty,
        foodCategory: formdata.foodCategory || formdata?.foodCategory.value,
        updatedAt: new Date(),
        createdAt: formdata?.createdAt,
        image: data.image || "",
      };
    } catch (e) {
      console.error("EditRecipe error:", e);
      return thunkAPI.rejectWithValue(e);
    }
  }
);

//delete doc
export const deleteDocument = createAsyncThunk(
  "Recipe/deleteDocument",
  async ({ id }, thunkAPI) => {
    try {
      await deleteDoc(doc(firestore, "recipe", id));

      return { id };
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const AddSchedule = createAsyncThunk(
  "Recipe/AddSchedule",
  async ({ formdata, uid }, thunkAPI) => {
    try {
      const storage = getStorage();
      let iconUrl = "";

      // ✅ Upload icon if it's a File
      if (formdata.icon && formdata.icon instanceof File) {
        const storageRef = ref(
          storage,
          `scheduleIcons/${uid}/${Date.now()}-${formdata.icon.name}`
        );
        await uploadBytes(storageRef, formdata.icon);
        iconUrl = await getDownloadURL(storageRef);
      } else if (typeof formdata.icon === "string") {
        iconUrl = formdata.icon;
      }

      const data = {
        adminId: uid,
        title: formdata.title ?? "",
        time: formdata.time ?? "",
        icon: iconUrl ?? "",
        createdAt: new Date(),
        normalizedId: formdata.normalizedId,
      };

      const docRef = await addDoc(
        collection(firestore, "customSchedules"),
        data
      );

      return {
        ...formdata,
        id: docRef.id,
        icon: iconUrl,
        createdAt: data.createdAt,
      };
    } catch (e) {
      console.error("AddSchedule Error:", e);
      return thunkAPI.rejectWithValue({
        message: e?.message || "Failed to add schedule",
      });
    }
  }
);

export const RecipeSlice = createSlice({
  name: "Recipe",
  initialState: {
    recipes: [],
    schedules: [],
    isFetching: false,
    isSuccess: false,
    isError: false,
    message: null,
  },
  reducers: {
    clearState: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
      state.message = null;
      return state;
    },
  },
  extraReducers: {
    [getrecipes.fulfilled]: (state, { payload }) => {
      state.recipes = payload ?? [];
      state.isSuccess = true;
      state.isFetching = false;
    },
    [getrecipes.pending]: (state) => {
      state.isFetching = true;
    },
    [getrecipes.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    [EditRecipe.fulfilled]: (state, { payload }) => {
      state.recipes = state.recipes.map((item) => {
        if (item.id === payload.id) {
          item = { ...payload };
        }
        return { ...item };
      });
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Recipe updated successfully";
    },

    [EditRecipe.pending]: (state) => {
      state.isFetching = true;
    },
    [EditRecipe.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    // AddMultipleRecipes

    [AddMultipleRecipes.fulfilled]: (state, { payload }) => {
      state.recipes = [...payload, ...state.recipes];
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Recipes added successfully";
    },
    [AddMultipleRecipes.pending]: (state) => {
      state.isFetching = false;
    },
    [AddMultipleRecipes.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    [AddRecipe.fulfilled]: (state, { payload }) => {
      state.recipes = [payload, ...state.recipes];
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Recipe added successfully";
      state.isError = false;
    },
    [AddRecipe.pending]: (state) => {
      state.isFetching = true;
    },
    [AddRecipe.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    [deleteDocument.fulfilled]: (state, { payload }) => {
      state.recipes = state.recipes.filter((item) => {
        if (item.id !== payload.id) {
          return { ...item };
        }
        return null;
      });
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Successfully deleted!!";
    },
    [deleteDocument.pending]: (state) => {
      state.isFetching = true;
    },
    [deleteDocument.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    [AddSchedule.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },
    [AddSchedule.fulfilled]: (state, { payload }) => {
      state.schedules = [payload, ...state.schedules];
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Schedule added successfully!";
      state.isError = false;
    },
    [AddSchedule.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error while adding schedule";
    },
  },
});

export const { clearState } = RecipeSlice.actions;

export const recipeSelector = (state) => state.recipes;
export const scheduleSelector = (state) => state.schedules;
